import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const HIGHLIGHT_CLASS = 'docs-search-highlight';
const HIGHLIGHT_STYLE_ID = 'docs-search-highlight-style';
const MAX_HIGHLIGHTS = 20;
const FADE_DELAY_MS = 3200;

// Inject the highlight + fade-out CSS once
const ensureHighlightStyle = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(HIGHLIGHT_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = HIGHLIGHT_STYLE_ID;
  el.textContent = `
    .${HIGHLIGHT_CLASS} {
      background: linear-gradient(90deg, #fde68a 0%, #fcd34d 100%);
      color: #1F4E79;
      padding: 0 2px;
      border-radius: 3px;
      box-shadow: 0 0 0 2px rgba(252, 211, 77, 0.45);
      transition: all 0.8s ease-out;
    }
    .${HIGHLIGHT_CLASS}.fade-out {
      background: transparent;
      box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
      color: inherit;
    }
  `;
  document.head.appendChild(el);
};

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Scans the given root for text nodes containing the term, wraps the FIRST
 * (and subsequent up to `max`) match in a highlight span.
 * Returns an array of the inserted spans so we can fade them later.
 */
const highlightInDOM = (root, term, max = MAX_HIGHLIGHTS) => {
  if (!root || !term || term.length < 2) return [];

  const inserted = [];
  const regex = new RegExp(`(${escapeRegExp(term)})`, 'i');

  // Skip scripts, styles, code blocks, and our playground forms (would mutate user data)
  const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'CODE', 'PRE', 'INPUT', 'TEXTAREA', 'NAV', 'BUTTON']);

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => {
      if (!n.nodeValue || !regex.test(n.nodeValue)) return NodeFilter.FILTER_REJECT;
      let p = n.parentNode;
      while (p && p !== root) {
        if (SKIP_TAGS.has(p.tagName)) return NodeFilter.FILTER_REJECT;
        if (p.classList && p.classList.contains(HIGHLIGHT_CLASS)) return NodeFilter.FILTER_REJECT;
        p = p.parentNode;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const candidates = [];
  let node = walker.nextNode();
  while (node && candidates.length < max) {
    candidates.push(node);
    node = walker.nextNode();
  }

  candidates.forEach((textNode) => {
    const text = textNode.nodeValue;
    const match = regex.exec(text);
    if (!match) return;
    const before = text.slice(0, match.index);
    const hit = match[0];
    const after = text.slice(match.index + hit.length);

    const frag = document.createDocumentFragment();
    if (before) frag.appendChild(document.createTextNode(before));
    const span = document.createElement('span');
    span.className = HIGHLIGHT_CLASS;
    span.textContent = hit;
    frag.appendChild(span);
    if (after) frag.appendChild(document.createTextNode(after));

    textNode.parentNode.replaceChild(frag, textNode);
    inserted.push(span);
  });

  return inserted;
};

const removeHighlights = (spans) => {
  spans.forEach((span) => {
    const parent = span.parentNode;
    if (!parent) return;
    parent.replaceChild(document.createTextNode(span.textContent), span);
    parent.normalize();
  });
};

/**
 * Hook used by container pages to briefly highlight text that matches the
 * `?q=` query string (set when the user arrived via the docs search modal).
 *
 * Flow: wait for paint → find ≤20 matches → scroll the first one into view →
 * fade-out class after 2.4s → remove spans after 3.2s → strip `q` param from URL.
 */
export const useSearchHighlight = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q');
    if (!query) return;

    ensureHighlightStyle();

    // Wait a tick so all content has rendered
    const renderTimer = setTimeout(() => {
      const root = document.querySelector('main') || document.body;
      const spans = highlightInDOM(root, query);
      if (spans.length === 0) {
        // Clean URL anyway
        params.delete('q');
        const clean = location.pathname + (params.toString() ? `?${params}` : '') + location.hash;
        window.history.replaceState(null, '', clean);
        return;
      }

      // Scroll first match into view (only if page wasn't already anchored)
      if (!location.hash) {
        const rect = spans[0].getBoundingClientRect();
        const y = rect.top + window.scrollY - 140;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }

      // Fade out, then remove
      const fadeTimer = setTimeout(() => {
        spans.forEach((s) => s.classList.add('fade-out'));
      }, FADE_DELAY_MS - 800);

      const removeTimer = setTimeout(() => {
        removeHighlights(spans);
        // Clean URL
        params.delete('q');
        const clean = location.pathname + (params.toString() ? `?${params}` : '') + location.hash;
        window.history.replaceState(null, '', clean);
      }, FADE_DELAY_MS);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(removeTimer);
        removeHighlights(spans);
      };
    }, 350);

    return () => clearTimeout(renderTimer);
  }, [location.pathname, location.search, location.hash]);
};
